/**
 * Relative dates, resolved.
 *
 * The app has drawn a calendar pill on the word "tomorrow" for a while, but it
 * was regex highlighting and nothing more — `due_date` existed on the task
 * model, in the REST payload and in the agent's editable-field list, and was
 * null on every task ever created. This module is the missing middle: it turns
 * the phrase into a date, so the badge can say "2 days overdue" instead of
 * repeating a word you typed three weeks ago.
 *
 * The phrase pattern is exported because SmartText highlights with it and this
 * file parses with it. One pattern, so what gets highlighted is exactly what
 * produced the stored date — there is no way for them to drift.
 */

const WEEKDAY_INDEX = {
    sun: 0, mon: 1, tues: 2, wednes: 3, thurs: 4, fri: 5, satur: 6,
};

const DATE_PHRASE_SOURCE =
    '\\b(?:' +
    'today|tonight|tomorrow|' +
    'in\\s+\\d+\\s+(?:day|week)s?|' +
    'next\\s+week|' +
    '(?:on|this|next)\\s+(?:sun|mon|tues|wednes|thurs|fri|satur)day|' +
    '(?:sun|mon|tues|wednes|thurs|fri|satur)day' +
    ')\\b';

/**
 * A fresh regex per call. These are `/g`, and a shared instance would carry
 * `lastIndex` between unrelated callers — the classic way a global regex starts
 * skipping matches in one place because something else used it first.
 */
export function datePhraseRegex() {
    return new RegExp(DATE_PHRASE_SOURCE, 'gi');
}

const DAY_MS = 86400000;

function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

/**
 * A due date is a calendar day, not an instant, and the two disagree.
 *
 * `new Date("2026-07-31")` is parsed as UTC midnight per spec, so west of
 * Greenwich it lands on the 30th; `toISOString()` on a local midnight has the
 * mirror-image problem. Both are the classic off-by-one-day bug.
 *
 * Every value this system stores starts with a plain YYYY-MM-DD — the parser
 * emits one, the agent is told to send one, and the API echoes it back with a
 * midnight time appended. So read the calendar fields straight off the front of
 * the string and build a local date from them, and no offset can move the day.
 */
export function toLocalDay(value) {
    // Not merely tidiness: `new Date(null)` is the epoch, and it is *valid*, so
    // without this an undated task resolves to 1 Jan 1970 — which reads as the
    // most overdue thing on the board and sorts to the very top of it.
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return startOfDay(value);
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
}

/**
 * The absolute day, spelled out: "31 Jul 2026".
 *
 * What `formatDue` says is relative and changes under you — that is the point of
 * it on a badge. It is the wrong answer anywhere the reader needs to know which
 * day was actually written down: a changeset diff saying "Today → In 2 days" is
 * unreadable a week later, and a tooltip is a promise of the precise value.
 *
 * Goes through `toLocalDay` for the reason that function exists — `new Date(str)`
 * on a bare YYYY-MM-DD is UTC midnight, which is the previous day everywhere west
 * of Greenwich.
 */
export function formatDay(value) {
    const day = toLocalDay(value);
    if (!day) return null;
    return day.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Local calendar day as YYYY-MM-DD — never `toISOString`, which shifts it. */
export function toDayString(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Whole days from `now`'s midnight to `due`'s midnight. Negative = past. */
function dayDelta(due, now) {
    const target = toLocalDay(due);
    if (!target) return null;
    return Math.round((target - startOfDay(now)) / DAY_MS);
}

/**
 * Resolve one matched phrase to a Date, or null if it isn't one we understand.
 * Re-parses the matched string rather than relying on capture-group indices, so
 * the pattern above can be extended without silently renumbering groups.
 */
export function parseDatePhrase(phrase, now = new Date()) {
    const p = String(phrase).trim().toLowerCase().replace(/\s+/g, ' ');
    const base = startOfDay(now);

    if (p === 'today' || p === 'tonight') return base;
    if (p === 'tomorrow') return addDays(base, 1);
    if (p === 'next week') return addDays(base, 7);

    const relative = /^in (\d+) (day|week)s?$/.exec(p);
    if (relative) {
        const n = parseInt(relative[1], 10);
        // "in 900 days" is a typo or a joke, not a due date.
        if (!Number.isFinite(n) || n < 1 || n > 365) return null;
        return addDays(base, relative[2] === 'week' ? n * 7 : n);
    }

    const weekday = /^(?:(on|this|next) )?(sun|mon|tues|wednes|thurs|fri|satur)day$/.exec(p);
    if (weekday) {
        const target = WEEKDAY_INDEX[weekday[2]];
        // Next occurrence, today included: saying "on friday" on a Friday means
        // today, not a week away. "next friday" always skips a week.
        let delta = (target - base.getDay() + 7) % 7;
        if (weekday[1] === 'next') delta += 7;
        return addDays(base, delta);
    }

    return null;
}

/**
 * The due date implied by what the user wrote. Title wins over notes, and the
 * first understood phrase wins — a title is short enough that the first date in
 * it is the one meant.
 *
 * Returns a YYYY-MM-DD string — the same shape the agent is told to send, so
 * both writers produce byte-identical values. Or null.
 */
export function resolveDueDate(title, description, now = new Date()) {
    for (const text of [title, description]) {
        if (!text) continue;
        const regex = datePhraseRegex();
        let match;
        while ((match = regex.exec(text)) !== null) {
            const parsed = parseDatePhrase(match[0], now);
            if (parsed) return toDayString(parsed);
        }
    }
    return null;
}

/** Filter buckets. Disjoint, so a task lands in exactly one. */
export const DUE_BUCKETS = [
    { id: 'overdue', label: 'Overdue' },
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'This week' },
    { id: 'none', label: 'No date' },
];

export function dueBucket(due, now = new Date()) {
    if (!due) return 'none';
    const days = dayDelta(due, now);
    if (days === null) return 'none';
    if (days < 0) return 'overdue';
    if (days === 0) return 'today';
    if (days <= 7) return 'week';
    return 'later';
}

/**
 * What the badge says right now. Derived from the stored date on every render,
 * never from the text — which is the whole point: the same task reads
 * "Tomorrow", then "Today", then "2 days overdue", without anyone editing it.
 */
export function formatDue(due, now = new Date()) {
    if (!due) return null;
    const days = dayDelta(due, now);
    if (days === null) return null;

    if (days < 0) {
        return {
            label: days === -1 ? '1 day late' : `${-days} days late`,
            chip: 'bg-red-500/20 text-red-400 border-red-500/40',
        };
    }
    if (days === 0) {
        return { label: 'Today', chip: 'bg-primary/20 text-primary border-primary/40' };
    }
    if (days === 1) {
        return { label: 'Tomorrow', chip: 'bg-accent/15 text-accent border-accent/30' };
    }
    if (days <= 7) {
        return { label: `In ${days} days`, chip: 'bg-accent/15 text-accent border-accent/30' };
    }
    return {
        label: toLocalDay(due).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
        chip: 'bg-white/5 text-muted-foreground border-white/10',
    };
}

/**
 * How long ago something happened: "today" / "3d" / "5w" / "7mo".
 *
 * The backlog's only column. Coarsens as it ages on purpose — once a task has
 * been parked for four months, the difference between 121 and 128 days is not
 * information, and a precise number invites you to act on noise.
 *
 * Goes through `toLocalDay` like everything else here, so a stored UTC midnight
 * doesn't read as a day older west of Greenwich.
 *
 * Mirrors describe_elapsed() in backend/task_dates.py.
 */
export function formatElapsed(since, now = new Date()) {
    if (!since) return '';
    const days = dayDelta(since, now);
    if (days === null) return '';

    const elapsed = -days;
    if (elapsed <= 0) return 'today';
    if (elapsed < 7) return `${elapsed}d`;
    if (elapsed < 60) return `${Math.floor(elapsed / 7)}w`;
    return `${Math.floor(elapsed / 30)}mo`;
}
